import { AIRecommendationsResponse } from "@/services/medications";
import {
  pharmacyService,
  VaccineProtocol,
  MedicationProtocol,
} from "@/services/pharmacy";
import { useState, useEffect } from "react";

export interface ManualSelection {
  type: "vaccine" | "medication";
  protocol: VaccineProtocol | MedicationProtocol;
  note: string;
  quantity?: string;
}

interface MedicationSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  aiRecommendations: AIRecommendationsResponse | null;
  medicationSelections: Record<string, boolean | null>;
  onSelectionChange: (medicationId: string, value: boolean | null, noteOverride?: string) => void;
  medicationNotes: Record<string, string>;
  onNoteChange: (medicationId: string, note: string) => void;
  onSaveSelections?: () => void;
  savingSelections?: boolean;
  manualSelections?: ManualSelection[];
  onManualSelectionAdd?: (selection: ManualSelection) => void;
  onManualSelectionRemove?: (protocolId: string) => void;
  // Batch update callbacks for committing all changes at once
  onBatchSelectionChange?: (selections: Record<string, boolean | null>) => void;
  onBatchNoteChange?: (notes: Record<string, string>) => void;
  onBatchManualSelectionsChange?: (selections: ManualSelection[]) => void;
  // Recommendation-level pharmacist note
  pharmacistRecommendationNote?: string;
  onRecommendationNoteChange?: (note: string) => void;
}

export function MedicationSelectionModal({
  isOpen,
  onClose,
  aiRecommendations,
  medicationSelections,
  onSelectionChange,
  medicationNotes,
  onNoteChange,
  onSaveSelections,
  savingSelections = false,
  manualSelections = [],
  onManualSelectionAdd,
  onManualSelectionRemove,
  onBatchSelectionChange,
  onBatchNoteChange,
  onBatchManualSelectionsChange,
  pharmacistRecommendationNote = "",
  onRecommendationNoteChange,
}: MedicationSelectionModalProps) {
  // Local state for temporary changes (only committed on save)
  const [localSelections, setLocalSelections] = useState<
    Record<string, boolean | null>
  >({});
  const [localNotes, setLocalNotes] = useState<Record<string, string>>({});
  const [localQuantities, setLocalQuantities] = useState<Record<string, string>>({});
  const [localManualSelections, setLocalManualSelections] = useState<
    ManualSelection[]
  >([]);
  const [localRecommendationNote, setLocalRecommendationNote] = useState("");
  const [hasInitialized, setHasInitialized] = useState(false);

  // Initialize local state when modal opens
  useEffect(() => {
    if (isOpen && !hasInitialized) {
      setLocalSelections({ ...medicationSelections });
      setLocalNotes({ ...medicationNotes });
      setLocalManualSelections([...manualSelections]);
      setLocalRecommendationNote(pharmacistRecommendationNote);
      setHasInitialized(true);
    }
    if (!isOpen) {
      setHasInitialized(false);
    }
  }, [
    isOpen,
    hasInitialized,
    medicationSelections,
    medicationNotes,
    manualSelections,
    pharmacistRecommendationNote,
  ]);

  // Local handlers that update temporary state AND immediately save to API
  const handleLocalSelectionChange = (
    medicationId: string,
    value: boolean | null,
  ) => {
    // Update local state for UI responsiveness
    setLocalSelections((prev) => ({
      ...prev,
      [medicationId]: value,
    }));

    // Get the current note from local state
    const currentNote = localNotes[medicationId] || "";

    // Sync the note to parent state
    if (currentNote !== medicationNotes[medicationId]) {
      onNoteChange(medicationId, currentNote);
    }

    // Immediately call parent handler to save to API, passing the note directly
    // This ensures the note is included even if state hasn't updated yet
    onSelectionChange(medicationId, value, currentNote);
  };

  const handleLocalNoteChange = (medicationId: string, note: string) => {
    setLocalNotes((prev) => ({
      ...prev,
      [medicationId]: note,
    }));
  };

  const handleLocalQuantityChange = (medicationId: string, quantity: string) => {
    setLocalQuantities((prev) => ({
      ...prev,
      [medicationId]: quantity,
    }));
  };

  // Save note for an item - called when user clicks save button or blurs the note input
  const handleSaveNote = (medicationId: string) => {
    const note = localNotes[medicationId] || "";
    // Update parent's note state
    onNoteChange(medicationId, note);

    // If item is already selected, re-save to update the note in the API
    const isSelected = localSelections[medicationId];
    if (isSelected === true || isSelected === false) {
      // Pass the note directly to ensure it's included in the API call
      onSelectionChange(medicationId, isSelected, note);
    }
  };

  const handleLocalManualSelectionAdd = (selection: ManualSelection) => {
    setLocalManualSelections((prev) => [...prev, selection]);
  };

  const handleLocalManualSelectionRemove = (protocolId: string) => {
    setLocalManualSelections((prev) =>
      prev.filter((s) => s.protocol.protocolId !== protocolId),
    );
  };

  // State for manual selection section
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedType, setSelectedType] = useState<
    "all" | "vaccine" | "medication"
  >("all");
  const [availableProtocols, setAvailableProtocols] = useState<{
    vaccines: VaccineProtocol[];
    medications: MedicationProtocol[];
  }>({ vaccines: [], medications: [] });
  const [loadingProtocols, setLoadingProtocols] = useState(false);
  const [showManualSection, setShowManualSection] = useState(true);
  const [expandedItems, setExpandedItems] = useState<Record<string, boolean>>(
    {},
  );
  const [enrichedProtocols, setEnrichedProtocols] = useState<
    Record<string, VaccineProtocol | MedicationProtocol>
  >({});

  const toggleItemExpanded = (itemKey: string) => {
    setExpandedItems((prev) => ({
      ...prev,
      [itemKey]: !prev[itemKey],
    }));
  };

  // Load protocols when manual section is shown
  useEffect(() => {
    if (
      showManualSection &&
      availableProtocols.vaccines.length === 0 &&
      availableProtocols.medications.length === 0
    ) {
      loadProtocols();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showManualSection]);

  // Fetch protocol details for pharmacist added items that are missing doses/route
  useEffect(() => {
    const enrichPharmacistAddedItems = async () => {
      if (!aiRecommendations) return;

      const itemsToEnrich: Array<{
        protocol_id: string;
        name: string;
        type: "vaccine" | "medication";
      }> = [];

      // Check pharmacist added vaccines
      (
        aiRecommendations?.recommendation?.pharmacistAddedVaccines || []
      ).forEach((vaccine) => {
        if (!vaccine.doses || !vaccine.route) {
          itemsToEnrich.push({
            protocol_id: vaccine.protocol_id,
            name: vaccine.name,
            type: "vaccine",
          });
        }
      });

      // Check pharmacist added medications
      (
        aiRecommendations?.recommendation?.pharmacistAddedMedications || []
      ).forEach((medication) => {
        if (!medication.doses || !medication.route) {
          itemsToEnrich.push({
            protocol_id: medication.protocol_id,
            name: medication.name,
            type: "medication",
          });
        }
      });

      if (itemsToEnrich.length === 0) return;

      // Fetch protocol details for each item
      const enrichmentPromises = itemsToEnrich.map(async (item) => {
        try {
          const response = await pharmacyService.getProtocols({
            search: item.name,
            type: item.type,
          });

          // Find the matching protocol
          const protocols =
            item.type === "vaccine"
              ? response.protocols.vaccines
              : response.protocols.medications;

          const matchingProtocol = protocols.find(
            (p) => p.protocolId === item.protocol_id,
          );

          if (matchingProtocol) {
            return {
              protocol_id: item.protocol_id,
              protocol: matchingProtocol,
            };
          }
        } catch (error) {
          console.error(`Failed to fetch protocol for ${item.name}:`, error);
        }
        return null;
      });

      const results = await Promise.all(enrichmentPromises);
      const newEnrichedProtocols: Record<
        string,
        VaccineProtocol | MedicationProtocol
      > = {};

      results.forEach((result) => {
        if (result) {
          newEnrichedProtocols[result.protocol_id] = result.protocol;
        }
      });

      if (Object.keys(newEnrichedProtocols).length > 0) {
        setEnrichedProtocols((prev) => ({ ...prev, ...newEnrichedProtocols }));
      }
    };

    enrichPharmacistAddedItems();
  }, [aiRecommendations]);

  const loadProtocols = async () => {
    setLoadingProtocols(true);
    try {
      const response = await pharmacyService.getProtocols(undefined);

      setAvailableProtocols({
        vaccines: response.protocols.vaccines,
        medications: response.protocols.medications,
      });
    } catch (error) {
      console.error("Failed to load protocols:", error);
    } finally {
      setLoadingProtocols(false);
    }
  };

  // Helper function to extract brand name from displayName
  const extractBrandName = (displayName: string): string => {
    // Extract text in parentheses as brand name
    const match = displayName.match(/\(([^)]+)\)/);
    return match ? match[1] : "Generic";
  };

  // Helper function to get route from protocol
  const getRoute = (protocol: VaccineProtocol | MedicationProtocol): string => {
    return protocol.dosing?.adult?.route || "Not specified";
  };

  // Helper function to get dose info from vaccine protocol
  const getDoseInfo = (protocol: VaccineProtocol): string => {
    return protocol.dosing?.adult?.dose || "Not specified";
  };

  // Helper function to get strength from medication protocol
  const getStrength = (protocol: MedicationProtocol): string => {
    return protocol.dosing?.adult?.dose || "Not specified";
  };

  // Helper function to get sig from medication protocol
  const getSig = (protocol: MedicationProtocol): string => {
    return protocol.dosing?.adult?.instructions || "Not specified";
  };

  // Get sets of already-existing protocol IDs for filtering
  const existingVaccineIds = new Set<string>([
    // AI recommended vaccines
    ...(aiRecommendations?.recommendation.vaccines.map((v) => v.protocol_id) || []),
    // NOT RECOMMENDED vaccines
    ...(aiRecommendations?.recommendation.notRecommended?.vaccines?.map((v) => v.protocol_id) || []),
    // Pharmacist added vaccines (previously saved)
    ...(aiRecommendations?.recommendation.pharmacistAddedVaccines?.map((v) => v.protocol_id) || []),
    // NOTE: Don't include localManualSelections here - we want to show them with "Added" badge
  ]);

  const existingMedicationIds = new Set<string>([
    // AI recommended medications
    ...(aiRecommendations?.recommendation.medications.map((m) => m.protocol_id) || []),
    // NOT RECOMMENDED medications
    ...(aiRecommendations?.recommendation.notRecommended?.medications?.map((m) => m.protocol_id) || []),
    // Pharmacist added medications (previously saved)
    ...(aiRecommendations?.recommendation.pharmacistAddedMedications?.map((m) => m.protocol_id) || []),
    // NOTE: Don't include localManualSelections here - we want to show them with "Added" badge
  ]);

  // Filter protocols based on search, type, and exclude already-existing items
  const filteredProtocols = {
    vaccines: availableProtocols.vaccines.filter(
      (v) =>
        (selectedType === "all" || selectedType === "vaccine") &&
        (searchTerm === "" ||
          v.displayName.toLowerCase().includes(searchTerm.toLowerCase())) &&
        !existingVaccineIds.has(v.protocolId),
    ),
    medications: availableProtocols.medications.filter(
      (m) =>
        (selectedType === "all" || selectedType === "medication") &&
        (searchTerm === "" ||
          m.displayName.toLowerCase().includes(searchTerm.toLowerCase())) &&
        !existingMedicationIds.has(m.protocolId),
    ),
  };

  const handleAddManualItem = (
    type: "vaccine" | "medication",
    protocol: VaccineProtocol | MedicationProtocol,
  ) => {
    handleLocalManualSelectionAdd({
      type,
      protocol,
      note: "",
      quantity: "",
    });
  };

  const isItemAlreadyAdded = (protocolId: string) => {
    return localManualSelections.some(
      (s) => s.protocol.protocolId === protocolId,
    );
  };

  const isItemInAIRecommendations = (protocolId: string) => {
    if (!aiRecommendations) return false;
    return (
      aiRecommendations?.recommendation?.vaccines.some(
        (v) => v.protocol_id === protocolId,
      ) ||
      aiRecommendations?.recommendation?.medications.some(
        (m) => m.protocol_id === protocolId,
      )
    );
  };

  if (!isOpen) return null;

  // Helper function to filter vaccines that should be shown
  // Includes RECOMMENDED, CONDITIONAL, NOT_RECOMMENDED, and CONTRAINDICATED vaccines that are required for entry
  const shouldShowVaccine = (vaccine: any) => {
    if (
      vaccine.status === "RECOMMENDED" ||
      vaccine.status === "CONDITIONAL" ||
      vaccine.status === "NOT_RECOMMENDED"
    ) {
      return true;
    }
    // Show contraindicated vaccines ONLY if required for entry
    if (vaccine.status === "CONTRAINDICATED") {
      return vaccine.flags?.includes("REQUIRED_FOR_ENTRY");
    }
    return false;
  };

  // Helper function to filter medications that should be shown
  const shouldShowMedication = (medication: any) => {
    if (
      medication.status === "RECOMMENDED" ||
      medication.status === "CONDITIONAL" ||
      medication.status === "NOT_RECOMMENDED"
    ) {
      return true;
    }
    return false;
  };

  const handleSaveAndClose = async () => {
    // Commit all local changes to parent state
    if (onBatchSelectionChange) {
      onBatchSelectionChange(localSelections);
    } else {
      // Fallback: apply changes one by one
      Object.entries(localSelections).forEach(([key, value]) => {
        if (medicationSelections[key] !== value) {
          onSelectionChange(key, value);
        }
      });
    }

    if (onBatchNoteChange) {
      onBatchNoteChange(localNotes);
    } else {
      // Fallback: apply changes one by one
      Object.entries(localNotes).forEach(([key, value]) => {
        if (medicationNotes[key] !== value) {
          onNoteChange(key, value);
        }
      });
    }

    if (onBatchManualSelectionsChange) {
      // Merge quantities and notes into manual selections before saving
      const manualSelectionsWithDetails = localManualSelections.map((selection) => {
        const selectionKey = `manual-${selection.type}-${selection.protocol.protocolId}`;
        return {
          ...selection,
          note: localNotes[selectionKey] || selection.note,
          quantity: localQuantities[selectionKey] || selection.quantity,
        };
      });
      onBatchManualSelectionsChange(manualSelectionsWithDetails);
    } else {
      // Fallback: sync manual selections
      // Remove items that were removed locally
      manualSelections.forEach((selection) => {
        if (
          !localManualSelections.some(
            (s) => s.protocol.protocolId === selection.protocol.protocolId,
          )
        ) {
          onManualSelectionRemove?.(selection.protocol.protocolId);
        }
      });
      // Add items that were added locally
      localManualSelections.forEach((selection) => {
        if (
          !manualSelections.some(
            (s) => s.protocol.protocolId === selection.protocol.protocolId,
          )
        ) {
          const selectionKey = `manual-${selection.type}-${selection.protocol.protocolId}`;
          const selectionWithDetails = {
            ...selection,
            note: localNotes[selectionKey] || selection.note,
            quantity: localQuantities[selectionKey] || selection.quantity,
          };
          onManualSelectionAdd?.(selectionWithDetails);
        }
      });
    }

    // Commit recommendation note change
    if (onRecommendationNoteChange && localRecommendationNote !== pharmacistRecommendationNote) {
      onRecommendationNoteChange(localRecommendationNote);
    }

    if (onSaveSelections) {
      await onSaveSelections();
    }
    onClose();
  };

  // Handle cancel - just close without saving
  const handleCancel = () => {
    // Reset local state to match parent state (discard changes)
    setLocalSelections({ ...medicationSelections });
    setLocalNotes({ ...medicationNotes });
    setLocalManualSelections([...manualSelections]);
    setLocalRecommendationNote(pharmacistRecommendationNote);
    onClose();
  };

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 9999,
        padding: "20px",
      }}
      onClick={handleCancel}
    >
      <div
        style={{
          backgroundColor: "white",
          borderRadius: "12px",
          width: "900px",
          maxWidth: "90vw",
          maxHeight: "90vh",
          display: "flex",
          flexDirection: "column",
          boxShadow:
            "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div
          style={{
            position: "sticky",
            top: 0,
            backgroundColor: "#14b8a6",
            padding: "16px 24px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            zIndex: 10,
          }}
        >
          <h3
            style={{
              margin: 0,
              fontSize: "17px",
              fontWeight: "600",
              color: "white",
            }}
          >
            Manually Add Vaccines/Medications
          </h3>
          <div style={{ display: "flex", gap: "8px" }}>
            <button
              onClick={handleSaveAndClose}
              disabled={savingSelections}
              style={{
                padding: "8px 18px",
                backgroundColor: "white",
                border: "none",
                borderRadius: "6px",
                fontSize: "13px",
                fontWeight: "600",
                cursor: savingSelections ? "not-allowed" : "pointer",
                color: "#14b8a6",
                opacity: savingSelections ? 0.7 : 1,
              }}
            >
              {savingSelections ? "Saving..." : "Save & Close"}
            </button>
            <button
              onClick={handleCancel}
              disabled={savingSelections}
              style={{
                padding: "6px 14px",
                backgroundColor: "rgba(255,255,255,0.2)",
                border: "none",
                borderRadius: "6px",
                fontSize: "13px",
                fontWeight: "500",
                cursor: savingSelections ? "not-allowed" : "pointer",
                color: "white",
                opacity: savingSelections ? 0.7 : 1,
              }}
            >
              Cancel
            </button>
          </div>
        </div>

        {/* Disclaimer */}
        <div
          style={{
            padding: "10px 20px",
            backgroundColor: "#eff6ff",
            borderBottom: "1px solid #bfdbfe",
          }}
        >
          <p
            style={{
              margin: 0,
              fontSize: "12px",
              color: "#1e40af",
              fontStyle: "italic",
              lineHeight: "1.4",
            }}
          >
            <strong>Note:</strong> Add vaccines or medications that are not in
            the AI recommendations. Only items not already recommended by AI are
            shown here.
          </p>
        </div>

        {/* Modal Content */}
        <div style={{ flex: 1, overflowY: "auto" }}>
          {!aiRecommendations && (
            <div
              style={{ padding: "40px", textAlign: "center", color: "#6b7280" }}
            >
              <p style={{ margin: 0 }}>Loading recommendations...</p>
            </div>
          )}

          {aiRecommendations && (
            <>
              {/* Vaccines Section - Hidden (selections moved to main section) */}
              {false &&
                ((aiRecommendations?.recommendation?.vaccines || []).filter(
                  shouldShowVaccine,
                ).length > 0 ||
                  localManualSelections.filter((s) => s.type === "vaccine")
                    .length > 0) && (
                  <div
                    style={{
                      padding: "16px 20px",
                      borderBottom: "1px solid #e5e7eb",
                    }}
                  >
                    <h4
                      style={{
                        fontSize: "16px",
                        fontWeight: "700",
                        color: "#1f2937",
                        marginBottom: "12px",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                      }}
                    >
                      Vaccines (
                      {(
                        aiRecommendations?.recommendation?.vaccines || []
                      ).filter(shouldShowVaccine).length +
                        (
                          aiRecommendations?.recommendation
                            .pharmacistAddedVaccines || []
                        ).filter((v) => {
                          const selectionKey = `manual-vaccine-${v.protocol_id}`;
                          return localSelections[selectionKey] !== false;
                        }).length +
                        localManualSelections
                          .filter((s) => s.type === "vaccine")
                          .filter(
                            (s) =>
                              !(
                                aiRecommendations?.recommendation
                                  .pharmacistAddedVaccines || []
                              ).some(
                                (v) => v.protocol_id === s.protocol.protocolId,
                              ),
                          )
                          .filter((s) => {
                            const selectionKey = `manual-vaccine-${s.protocol.protocolId}`;
                            return localSelections[selectionKey] !== false;
                          }).length}
                      )
                    </h4>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      {/* AI Recommended Vaccines */}
                      {aiRecommendations?.recommendation?.vaccines
                        .filter(shouldShowVaccine)
                        .filter(
                          (vaccine) =>
                            searchTerm === "" ||
                            vaccine.vaccine_name
                              .toLowerCase()
                              .includes(searchTerm.toLowerCase()),
                        )
                        .map((vaccine) => {
                          const selectionKey = `vaccine-${vaccine.protocol_id}`;
                          const selectionValue = localSelections[selectionKey];
                          const isExpanded = expandedItems[selectionKey];

                          return (
                            <div
                              key={vaccine.protocol_id}
                              style={{
                                padding: "12px 14px",
                                borderRadius: "8px",
                                marginBottom: "8px",
                                border: "1px solid #e5e7eb",
                                backgroundColor: "#fafafa",
                              }}
                            >
                              {/* Collapsed Header - Always Visible */}
                              <div
                                style={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                  gap: "12px",
                                }}
                              >
                                <div
                                  onClick={() =>
                                    toggleItemExpanded(selectionKey)
                                  }
                                  style={{
                                    flex: 1,
                                    cursor: "pointer",
                                    minWidth: 0,
                                  }}
                                >
                                  <div
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: "8px",
                                      marginBottom: "4px",
                                    }}
                                  >
                                    <span
                                      style={{
                                        fontSize: "16px",
                                        color: "#6b7280",
                                        fontWeight: "bold",
                                        flexShrink: 0,
                                      }}
                                    >
                                      {isExpanded ? "▼" : "▶"}
                                    </span>
                                    <div
                                      style={{
                                        fontWeight: "600",
                                        fontSize: "14px",
                                        color: "#1f2937",
                                      }}
                                    >
                                      {vaccine.vaccine_name}
                                    </div>
                                    {vaccine.status === "CONTRAINDICATED" &&
                                      vaccine.flags?.includes(
                                        "REQUIRED_FOR_ENTRY",
                                      ) && (
                                        <span
                                          style={{
                                            padding: "3px 8px",
                                            borderRadius: "4px",
                                            fontSize: "10px",
                                            fontWeight: "700",
                                            backgroundColor: "#fee2e2",
                                            color: "#991b1b",
                                            border: "1px solid #fca5a5",
                                            flexShrink: 0,
                                          }}
                                        >
                                          Required for Entry
                                        </span>
                                      )}
                                  </div>
                                  {/* Key Info - Always visible */}
                                  <div
                                    style={{
                                      fontSize: "11px",
                                      color: "#6b7280",
                                      marginLeft: "24px",
                                    }}
                                  >
                                    {vaccine.dose} • {vaccine.route} •{" "}
                                    {vaccine.timing || "As scheduled"}
                                  </div>
                                </div>

                                {/* Selection buttons - Always visible */}
                                <div
                                  style={{
                                    display: "flex",
                                    gap: "6px",
                                    flexShrink: 0,
                                  }}
                                >
                                  <button
                                    onClick={() =>
                                      handleLocalSelectionChange(
                                        selectionKey,
                                        true,
                                      )
                                    }
                                    style={{
                                      padding: "6px 12px",
                                      border: "1px solid #d1d5db",
                                      borderRadius: "6px",
                                      fontSize: "12px",
                                      fontWeight: "500",
                                      cursor: "pointer",
                                      backgroundColor:
                                        selectionValue === true
                                          ? "#dcfce7"
                                          : "white",
                                      color:
                                        selectionValue === true
                                          ? "#166534"
                                          : "#6b7280",
                                      transition: "all 0.2s",
                                    }}
                                  >
                                    Yes
                                  </button>
                                  <button
                                    onClick={() =>
                                      handleLocalSelectionChange(
                                        selectionKey,
                                        false,
                                      )
                                    }
                                    style={{
                                      padding: "6px 12px",
                                      border: "1px solid #d1d5db",
                                      borderRadius: "6px",
                                      fontSize: "12px",
                                      fontWeight: "500",
                                      cursor: "pointer",
                                      backgroundColor:
                                        selectionValue === false
                                          ? "#fee2e2"
                                          : "white",
                                      color:
                                        selectionValue === false
                                          ? "#991b1b"
                                          : "#6b7280",
                                      transition: "all 0.2s",
                                    }}
                                  >
                                    No
                                  </button>
                                </div>
                              </div>

                              {/* Expanded Details - Conditionally visible */}
                              {isExpanded && (
                                <div
                                  style={{
                                    marginTop: "12px",
                                    marginLeft: "24px",
                                  }}
                                >
                                  {vaccine.reasoning && (
                                    <div
                                      style={{
                                        fontSize: "12px",
                                        color: "#6b7280",
                                        marginBottom: "8px",
                                      }}
                                    >
                                      {vaccine.reasoning}
                                    </div>
                                  )}

                                  {/* Vaccine Details Grid */}
                                  <div
                                    style={{
                                      marginBottom: "8px",
                                      padding: "8px",
                                      backgroundColor:
                                        "rgba(255, 255, 255, 0.7)",
                                      borderRadius: "6px",
                                      display: "grid",
                                      gridTemplateColumns: "80px 1fr",
                                      gap: "6px",
                                    }}
                                  >
                                    <div style={{ marginBottom: "0" }}>
                                      <span
                                        style={{
                                          fontSize: "11px",
                                          color: "#6b7280",
                                          fontWeight: "600",
                                        }}
                                      >
                                        Dose:{" "}
                                      </span>
                                      <span
                                        style={{
                                          fontSize: "11px",
                                          color: "#1f2937",
                                          fontWeight: "600",
                                        }}
                                      >
                                        {vaccine.dose}
                                      </span>
                                    </div>
                                    <div style={{ marginBottom: "0" }}>
                                      <span
                                        style={{
                                          fontSize: "11px",
                                          color: "#6b7280",
                                          fontWeight: "600",
                                        }}
                                      >
                                        Route:{" "}
                                      </span>
                                      <span
                                        style={{
                                          fontSize: "11px",
                                          color: "#1f2937",
                                          fontWeight: "600",
                                        }}
                                      >
                                        {vaccine.route}
                                      </span>
                                    </div>
                                    <div style={{ gridColumn: "1 / -1" }}>
                                      <span
                                        style={{
                                          fontSize: "11px",
                                          color: "#6b7280",
                                          fontWeight: "600",
                                        }}
                                      >
                                        Timing:{" "}
                                      </span>
                                      <span
                                        style={{
                                          fontSize: "11px",
                                          color: "#1f2937",
                                          fontWeight: "600",
                                        }}
                                      >
                                        {vaccine.timing || "As scheduled"}
                                      </span>
                                    </div>
                                  </div>

                                  {/* Series Schedule */}
                                  {vaccine.series_schedule && (
                                    <div
                                      style={{
                                        marginBottom: "8px",
                                        padding: "8px",
                                        backgroundColor:
                                          "rgba(255, 255, 255, 0.7)",
                                        borderRadius: "6px",
                                      }}
                                    >
                                      <div
                                        style={{
                                          fontWeight: "600",
                                          color: "#374151",
                                          fontSize: "11px",
                                          marginBottom: "4px",
                                        }}
                                      >
                                        Series Schedule:
                                      </div>
                                      <div
                                        style={{
                                          color: "#6b7280",
                                          fontSize: "11px",
                                        }}
                                      >
                                        {vaccine.series_schedule}
                                      </div>
                                    </div>
                                  )}

                                  {/* Counseling Points */}
                                  {vaccine.counseling_points?.length > 0 && (
                                    <div
                                      style={{
                                        marginBottom: "8px",
                                        padding: "8px",
                                        backgroundColor:
                                          "rgba(255, 255, 255, 0.7)",
                                        borderRadius: "6px",
                                      }}
                                    >
                                      <div
                                        style={{
                                          fontWeight: "600",
                                          color: "#374151",
                                          fontSize: "11px",
                                          marginBottom: "4px",
                                        }}
                                      >
                                        Counseling Points:
                                      </div>
                                      <ul
                                        style={{
                                          margin: 0,
                                          paddingLeft: "16px",
                                          fontSize: "11px",
                                          color: "#6b7280",
                                        }}
                                      >
                                        {vaccine.counseling_points.map(
                                          (point, i) => (
                                            <li
                                              key={i}
                                              style={{ marginBottom: "2px" }}
                                            >
                                              {point}
                                            </li>
                                          ),
                                        )}
                                      </ul>
                                    </div>
                                  )}

                                  {/* Flags Warning */}
                                  {vaccine.flags?.length > 0 && (
                                    <div
                                      style={{
                                        padding: "8px",
                                        backgroundColor: "#fef2f2",
                                        border: "1px solid #fecaca",
                                        borderRadius: "6px",
                                        fontSize: "11px",
                                        color: "#dc2626",
                                        marginBottom: "8px",
                                      }}
                                    >
                                      <div
                                        style={{
                                          fontWeight: "600",
                                          marginBottom: "4px",
                                        }}
                                      >
                                        Important:
                                      </div>
                                      {vaccine.flags.map((flag, i) => (
                                        <div
                                          key={i}
                                          style={{ marginBottom: "2px" }}
                                        >
                                          • {flag}
                                        </div>
                                      ))}
                                    </div>
                                  )}

                                  {/* Pharmacist Note */}
                                  <div>
                                    <label
                                      htmlFor={`note-${selectionKey}`}
                                      style={{
                                        display: "block",
                                        fontSize: "11px",
                                        fontWeight: "600",
                                        color: "#374151",
                                        marginBottom: "4px",
                                      }}
                                    >
                                      Pharmacist Note (Optional):
                                    </label>
                                    <textarea
                                      id={`note-${selectionKey}`}
                                      value={localNotes[selectionKey] || ""}
                                      onChange={(e) =>
                                        handleLocalNoteChange(
                                          selectionKey,
                                          e.target.value,
                                        )
                                      }
                                      onBlur={() => handleSaveNote(selectionKey)}
                                      placeholder="Add any notes or special instructions..."
                                      style={{
                                        width: "100%",
                                        padding: "6px 8px",
                                        border: "1px solid #d1d5db",
                                        borderRadius: "4px",
                                        fontSize: "11px",
                                        resize: "vertical",
                                        minHeight: "50px",
                                        fontFamily: "inherit",
                                      }}
                                    />
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}

                      {/* Pharmacist Added Vaccines from API (previously saved) */}
                      {(
                        aiRecommendations?.recommendation
                          .pharmacistAddedVaccines || []
                      )
                        .filter((vaccine) => {
                          const selectionKey = `manual-vaccine-${vaccine.protocol_id}`;
                          return localSelections[selectionKey] !== false;
                        })
                        .filter(
                          (vaccine) =>
                            searchTerm === "" ||
                            vaccine.name
                              .toLowerCase()
                              .includes(searchTerm.toLowerCase()),
                        )
                        .map((vaccine) => {
                          const selectionKey = `manual-vaccine-${vaccine.protocol_id}`;
                          const isExpanded = expandedItems[selectionKey];

                          return (
                            <div
                              key={vaccine.protocol_id}
                              style={{
                                padding: "12px 14px",
                                borderRadius: "8px",
                                marginBottom: "8px",
                                border: "2px solid #14b8a6",
                                backgroundColor: "#f0fdfa",
                                position: "relative",
                              }}
                            >
                              <div
                                style={{
                                  position: "absolute",
                                  top: "8px",
                                  right: "8px",
                                  padding: "3px 8px",
                                  borderRadius: "4px",
                                  fontSize: "10px",
                                  fontWeight: "700",
                                  backgroundColor: "#14b8a6",
                                  color: "white",
                                }}
                              >
                                PHARMACIST ADDED
                              </div>

                              {/* Collapsed Header - Always Visible */}
                              <div
                                style={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                  gap: "12px",
                                  marginTop: "20px",
                                }}
                              >
                                <div
                                  onClick={() =>
                                    toggleItemExpanded(selectionKey)
                                  }
                                  style={{
                                    flex: 1,
                                    cursor: "pointer",
                                    minWidth: 0,
                                  }}
                                >
                                  <div
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: "8px",
                                      marginBottom: "4px",
                                    }}
                                  >
                                    <span
                                      style={{
                                        fontSize: "16px",
                                        color: "#6b7280",
                                        fontWeight: "bold",
                                        flexShrink: 0,
                                      }}
                                    >
                                      {isExpanded ? "▼" : "▶"}
                                    </span>
                                    <div
                                      style={{
                                        fontWeight: "600",
                                        fontSize: "14px",
                                        color: "#1f2937",
                                      }}
                                    >
                                      {vaccine.name}
                                    </div>
                                  </div>
                                  {/* Key Info - Always visible */}
                                  <div
                                    style={{
                                      fontSize: "11px",
                                      color: "#6b7280",
                                      marginLeft: "24px",
                                    }}
                                  >
                                    {vaccine.doses ||
                                      (
                                        enrichedProtocols[
                                          vaccine.protocol_id
                                        ] as VaccineProtocol
                                      )?.dosing?.adult?.dose ||
                                      "Not specified"}{" "}
                                    •{" "}
                                    {vaccine.route ||
                                      enrichedProtocols[vaccine.protocol_id]
                                        ?.dosing?.adult?.route ||
                                      "Not specified"}
                                  </div>
                                </div>

                                {/* Selection buttons */}
                                <div
                                  style={{
                                    display: "flex",
                                    gap: "6px",
                                    flexShrink: 0,
                                  }}
                                >
                                  <button
                                    onClick={() =>
                                      handleLocalSelectionChange(
                                        selectionKey,
                                        true,
                                      )
                                    }
                                    style={{
                                      padding: "6px 12px",
                                      border: "1px solid #d1d5db",
                                      borderRadius: "6px",
                                      fontSize: "12px",
                                      fontWeight: "500",
                                      cursor: "pointer",
                                      backgroundColor:
                                        localSelections[selectionKey] === true
                                          ? "#dcfce7"
                                          : "white",
                                      color:
                                        localSelections[selectionKey] === true
                                          ? "#166534"
                                          : "#6b7280",
                                      transition: "all 0.2s",
                                    }}
                                  >
                                    Yes
                                  </button>
                                  <button
                                    onClick={() =>
                                      handleLocalSelectionChange(
                                        selectionKey,
                                        false,
                                      )
                                    }
                                    style={{
                                      padding: "6px 12px",
                                      border: "1px solid #d1d5db",
                                      borderRadius: "6px",
                                      fontSize: "12px",
                                      fontWeight: "500",
                                      cursor: "pointer",
                                      backgroundColor:
                                        localSelections[selectionKey] === false
                                          ? "#fee2e2"
                                          : "white",
                                      color:
                                        localSelections[selectionKey] === false
                                          ? "#991b1b"
                                          : "#6b7280",
                                      transition: "all 0.2s",
                                    }}
                                  >
                                    No
                                  </button>
                                </div>
                              </div>

                              {/* Expanded Details - Conditionally visible */}
                              {isExpanded && (
                                <div
                                  style={{
                                    marginTop: "12px",
                                    marginLeft: "24px",
                                  }}
                                >
                                  {/* Vaccine Details */}
                                  <div
                                    style={{
                                      marginBottom: "8px",
                                      padding: "8px",
                                      backgroundColor:
                                        "rgba(255, 255, 255, 0.7)",
                                      borderRadius: "6px",
                                      display: "grid",
                                      gridTemplateColumns: "80px 1fr",
                                      gap: "6px",
                                    }}
                                  >
                                    <div>
                                      <span
                                        style={{
                                          fontSize: "11px",
                                          color: "#6b7280",
                                          fontWeight: "600",
                                        }}
                                      >
                                        Route:{" "}
                                      </span>
                                      <span
                                        style={{
                                          fontSize: "11px",
                                          color: "#1f2937",
                                          fontWeight: "600",
                                        }}
                                      >
                                        {vaccine.route ||
                                          enrichedProtocols[vaccine.protocol_id]
                                            ?.dosing?.adult?.route ||
                                          "Not specified"}
                                      </span>
                                    </div>
                                    <div>
                                      <span
                                        style={{
                                          fontSize: "11px",
                                          color: "#6b7280",
                                          fontWeight: "600",
                                        }}
                                      >
                                        Doses:{" "}
                                      </span>
                                      <span
                                        style={{
                                          fontSize: "11px",
                                          color: "#1f2937",
                                          fontWeight: "600",
                                        }}
                                      >
                                        {vaccine.doses ||
                                          (
                                            enrichedProtocols[
                                              vaccine.protocol_id
                                            ] as VaccineProtocol
                                          )?.dosing?.adult?.dose ||
                                          "Not specified"}
                                      </span>
                                    </div>
                                  </div>

                                  {/* Brands */}
                                  {vaccine.brands &&
                                    vaccine.brands.length > 0 && (
                                      <div
                                        style={{
                                          marginBottom: "8px",
                                          padding: "8px",
                                          backgroundColor:
                                            "rgba(255, 255, 255, 0.7)",
                                          borderRadius: "6px",
                                        }}
                                      >
                                        <div
                                          style={{
                                            fontWeight: "600",
                                            color: "#374151",
                                            fontSize: "11px",
                                            marginBottom: "4px",
                                          }}
                                        >
                                          Brands:
                                        </div>
                                        <div
                                          style={{
                                            color: "#6b7280",
                                            fontSize: "11px",
                                          }}
                                        >
                                          {vaccine.brands.join(", ")}
                                        </div>
                                      </div>
                                    )}

                                  {/* Pharmacist Note */}
                                  <div>
                                    <label
                                      htmlFor={`note-${selectionKey}`}
                                      style={{
                                        display: "block",
                                        fontSize: "11px",
                                        fontWeight: "600",
                                        color: "#374151",
                                        marginBottom: "4px",
                                      }}
                                    >
                                      Pharmacist Note (Optional):
                                    </label>
                                    <textarea
                                      id={`note-${selectionKey}`}
                                      value={localNotes[selectionKey] || ""}
                                      onChange={(e) =>
                                        handleLocalNoteChange(
                                          selectionKey,
                                          e.target.value,
                                        )
                                      }
                                      onBlur={() => handleSaveNote(selectionKey)}
                                      placeholder="Add any notes or special instructions..."
                                      style={{
                                        width: "100%",
                                        padding: "6px 8px",
                                        border: "1px solid #d1d5db",
                                        borderRadius: "4px",
                                        fontSize: "11px",
                                        resize: "vertical",
                                        minHeight: "50px",
                                        fontFamily: "inherit",
                                      }}
                                    />
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}

                      {/* Manual Vaccine Selections (newly added in this session) */}
                      {localManualSelections
                        .filter((s) => s.type === "vaccine")
                        .filter(
                          (selection) =>
                            searchTerm === "" ||
                            selection.protocol.displayName
                              .toLowerCase()
                              .includes(searchTerm.toLowerCase()),
                        )
                        // Filter out items that are already in pharmacistAddedVaccines
                        .filter(
                          (selection) =>
                            !(
                              aiRecommendations?.recommendation
                                .pharmacistAddedVaccines || []
                            ).some(
                              (v) =>
                                v.protocol_id === selection.protocol.protocolId,
                            ),
                        )
                        // Filter out items where user selected "No" (false)
                        .filter((selection) => {
                          const selectionKey = `manual-vaccine-${selection.protocol.protocolId}`;
                          return localSelections[selectionKey] !== false;
                        })
                        .map((selection) => {
                          const selectionKey = `manual-vaccine-${selection.protocol.protocolId}`;
                          const isExpanded = expandedItems[selectionKey];
                          const protocol =
                            selection.protocol as VaccineProtocol;

                          return (
                            <div
                              key={selection.protocol.protocolId}
                              style={{
                                padding: "12px 14px",
                                borderRadius: "8px",
                                marginBottom: "8px",
                                border: "2px dashed #14b8a6",
                                backgroundColor: "#f0fdfa",
                                position: "relative",
                              }}
                            >
                              {/* Collapsed Header - Always Visible */}
                              <div
                                style={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                  gap: "12px",
                                }}
                              >
                                <div
                                  onClick={() =>
                                    toggleItemExpanded(selectionKey)
                                  }
                                  style={{
                                    flex: 1,
                                    cursor: "pointer",
                                    minWidth: 0,
                                  }}
                                >
                                  <div
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: "8px",
                                      marginBottom: "4px",
                                    }}
                                  >
                                    <span
                                      style={{
                                        fontSize: "16px",
                                        color: "#6b7280",
                                        fontWeight: "bold",
                                        flexShrink: 0,
                                      }}
                                    >
                                      {isExpanded ? "▼" : "▶"}
                                    </span>
                                    <div
                                      style={{
                                        fontWeight: "600",
                                        fontSize: "14px",
                                        color: "#1f2937",
                                      }}
                                    >
                                      {selection.protocol.displayName}
                                    </div>
                                  </div>
                                  {/* Key Info - Always visible */}
                                  <div
                                    style={{
                                      fontSize: "11px",
                                      color: "#6b7280",
                                      marginLeft: "24px",
                                    }}
                                  >
                                    {getDoseInfo(protocol)} •{" "}
                                    {getRoute(protocol)}
                                  </div>
                                </div>

                                {/* Selection buttons - Yes keeps it, No/Remove removes it */}
                                <div
                                  style={{
                                    display: "flex",
                                    gap: "6px",
                                    flexShrink: 0,
                                  }}
                                >
                                  <button
                                    onClick={() =>
                                      handleLocalSelectionChange(
                                        selectionKey,
                                        true,
                                      )
                                    }
                                    style={{
                                      padding: "6px 12px",
                                      border: "1px solid #d1d5db",
                                      borderRadius: "6px",
                                      fontSize: "12px",
                                      fontWeight: "500",
                                      cursor: "pointer",
                                      backgroundColor:
                                        localSelections[selectionKey] === true
                                          ? "#dcfce7"
                                          : "white",
                                      color:
                                        localSelections[selectionKey] === true
                                          ? "#166534"
                                          : "#6b7280",
                                      transition: "all 0.2s",
                                    }}
                                  >
                                    Yes
                                  </button>
                                  <button
                                    onClick={() => {
                                      // Clicking No on a manually added item removes it
                                      handleLocalSelectionChange(
                                        selectionKey,
                                        false,
                                      );
                                      handleLocalManualSelectionRemove(
                                        selection.protocol.protocolId,
                                      );
                                    }}
                                    style={{
                                      padding: "6px 12px",
                                      backgroundColor: "#fee2e2",
                                      color: "#991b1b",
                                      border: "none",
                                      borderRadius: "6px",
                                      fontSize: "12px",
                                      fontWeight: "600",
                                      cursor: "pointer",
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    No / Remove
                                  </button>
                                </div>
                              </div>

                              {/* Expanded Details - Conditionally visible */}
                              {isExpanded && (
                                <div
                                  style={{
                                    marginTop: "12px",
                                    marginLeft: "24px",
                                  }}
                                >
                                  {/* Protocol Information */}
                                  {protocol.notes && (
                                    <div
                                      style={{
                                        fontSize: "12px",
                                        color: "#6b7280",
                                        marginBottom: "8px",
                                      }}
                                    >
                                      {protocol.notes}
                                    </div>
                                  )}

                                  {/* Vaccine Details */}
                                  <div
                                    style={{
                                      marginBottom: "8px",
                                      padding: "8px",
                                      backgroundColor:
                                        "rgba(255, 255, 255, 0.7)",
                                      borderRadius: "6px",
                                      display: "grid",
                                      gridTemplateColumns: "80px 1fr",
                                      gap: "6px",
                                    }}
                                  >
                                    <div>
                                      <span
                                        style={{
                                          fontSize: "11px",
                                          color: "#6b7280",
                                          fontWeight: "600",
                                        }}
                                      >
                                        Route:{" "}
                                      </span>
                                      <span
                                        style={{
                                          fontSize: "11px",
                                          color: "#1f2937",
                                          fontWeight: "600",
                                        }}
                                      >
                                        {getRoute(protocol)}
                                      </span>
                                    </div>
                                    <div>
                                      <span
                                        style={{
                                          fontSize: "11px",
                                          color: "#6b7280",
                                          fontWeight: "600",
                                        }}
                                      >
                                        Dose:{" "}
                                      </span>
                                      <span
                                        style={{
                                          fontSize: "11px",
                                          color: "#1f2937",
                                          fontWeight: "600",
                                        }}
                                      >
                                        {getDoseInfo(protocol)}
                                      </span>
                                    </div>
                                    {protocol.dosing?.adult?.instructions && (
                                      <div style={{ gridColumn: "1 / -1" }}>
                                        <span
                                          style={{
                                            fontSize: "11px",
                                            color: "#6b7280",
                                            fontWeight: "600",
                                          }}
                                        >
                                          Instructions:{" "}
                                        </span>
                                        <span
                                          style={{
                                            fontSize: "11px",
                                            color: "#1f2937",
                                            fontWeight: "600",
                                          }}
                                        >
                                          {protocol.dosing.adult.instructions}
                                        </span>
                                      </div>
                                    )}
                                  </div>

                                  {/* Brands */}
                                  <div
                                    style={{
                                      marginBottom: "8px",
                                      padding: "8px",
                                      backgroundColor:
                                        "rgba(255, 255, 255, 0.7)",
                                      borderRadius: "6px",
                                    }}
                                  >
                                    <div
                                      style={{
                                        fontWeight: "600",
                                        color: "#374151",
                                        fontSize: "11px",
                                        marginBottom: "4px",
                                      }}
                                    >
                                      Brands:
                                    </div>
                                    <div
                                      style={{
                                        color: "#6b7280",
                                        fontSize: "11px",
                                      }}
                                    >
                                      {extractBrandName(protocol.displayName)}
                                    </div>
                                  </div>

                                  {/* Quantity */}
                                  {selection.type === "medication" && (
                                    <div>
                                      <label
                                        htmlFor={`quantity-${selectionKey}`}
                                        style={{
                                          display: "block",
                                          fontSize: "11px",
                                          fontWeight: "600",
                                          color: "#374151",
                                          marginBottom: "4px",
                                        }}
                                      >
                                        Quantity:
                                      </label>
                                      <input
                                        id={`quantity-${selectionKey}`}
                                        type="text"
                                        inputMode="numeric"
                                        value={localQuantities[selectionKey] || ""}
                                        onChange={(e) => {
                                          const val = e.target.value;
                                          // Allow empty string (clearing) or digits only
                                          if (val === "" || /^\d+$/.test(val)) {
                                            handleLocalQuantityChange(selectionKey, val);
                                          }
                                        }}
                                        placeholder="e.g., 30"
                                        style={{
                                          width: "100%",
                                          padding: "6px 8px",
                                          border: "1px solid #d1d5db",
                                          borderRadius: "4px",
                                          fontSize: "11px",
                                          fontFamily: "inherit",
                                        }}
                                      />
                                    </div>
                                  )}

                                  {/* Pharmacist Note */}
                                  <div>
                                    <label
                                      htmlFor={`note-${selectionKey}`}
                                      style={{
                                        display: "block",
                                        fontSize: "11px",
                                        fontWeight: "600",
                                        color: "#374151",
                                        marginBottom: "4px",
                                      }}
                                    >
                                      Pharmacist Note (Optional):
                                    </label>
                                    <textarea
                                      id={`note-${selectionKey}`}
                                      value={localNotes[selectionKey] || ""}
                                      onChange={(e) =>
                                        handleLocalNoteChange(
                                          selectionKey,
                                          e.target.value,
                                        )
                                      }
                                      onBlur={() => handleSaveNote(selectionKey)}
                                      placeholder="Add any notes or special instructions..."
                                      style={{
                                        width: "100%",
                                        padding: "6px 8px",
                                        border: "1px solid #d1d5db",
                                        borderRadius: "4px",
                                        fontSize: "11px",
                                        resize: "vertical",
                                        minHeight: "50px",
                                        fontFamily: "inherit",
                                      }}
                                    />
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                    </div>
                  </div>
                )}

              {/* Medications Section - Hidden (selections moved to main section) */}
              {false &&
                ((aiRecommendations?.recommendation?.medications || []).filter(
                  (m) =>
                    m.status === "RECOMMENDED" || m.status === "CONDITIONAL",
                ).length > 0 ||
                  localManualSelections.filter((s) => s.type === "medication")
                    .length > 0) && (
                  <div
                    style={{
                      padding: "16px 20px",
                    }}
                  >
                    <h4
                      style={{
                        fontSize: "16px",
                        fontWeight: "700",
                        color: "#1f2937",
                        marginBottom: "12px",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                      }}
                    >
                      Medications (
                      {(
                        aiRecommendations?.recommendation?.medications || []
                      ).filter(
                        (m) =>
                          m.status === "RECOMMENDED" ||
                          m.status === "CONDITIONAL",
                      ).length +
                        (
                          aiRecommendations?.recommendation
                            .pharmacistAddedMedications || []
                        ).filter((m) => {
                          const selectionKey = `manual-medication-${m.protocol_id}`;
                          return localSelections[selectionKey] !== false;
                        }).length +
                        localManualSelections
                          .filter((s) => s.type === "medication")
                          .filter(
                            (s) =>
                              !(
                                aiRecommendations?.recommendation
                                  .pharmacistAddedMedications || []
                              ).some(
                                (m) => m.protocol_id === s.protocol.protocolId,
                              ),
                          )
                          .filter((s) => {
                            const selectionKey = `manual-medication-${s.protocol.protocolId}`;
                            return localSelections[selectionKey] !== false;
                          }).length}
                      )
                    </h4>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      {/* AI Recommended Medications */}
                      {aiRecommendations?.recommendation?.medications
                        .filter(shouldShowMedication)
                        .filter(
                          (med) =>
                            searchTerm === "" ||
                            med.medication_name
                              .toLowerCase()
                              .includes(searchTerm.toLowerCase()),
                        )
                        .map((med) => {
                          const selectionKey = `medication-${med.protocol_id}`;
                          const selectionValue = localSelections[selectionKey];
                          const isExpanded = expandedItems[selectionKey];

                          return (
                            <div
                              key={med.protocol_id}
                              style={{
                                padding: "12px 14px",
                                borderRadius: "8px",
                                marginBottom: "8px",
                                border: "1px solid #e5e7eb",
                                backgroundColor: "#fafafa",
                              }}
                            >
                              {/* Collapsed Header - Always Visible */}
                              <div
                                style={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                  gap: "12px",
                                }}
                              >
                                <div
                                  onClick={() =>
                                    toggleItemExpanded(selectionKey)
                                  }
                                  style={{
                                    flex: 1,
                                    cursor: "pointer",
                                    minWidth: 0,
                                  }}
                                >
                                  <div
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: "8px",
                                      marginBottom: "4px",
                                    }}
                                  >
                                    <span
                                      style={{
                                        fontSize: "16px",
                                        color: "#6b7280",
                                        fontWeight: "bold",
                                        flexShrink: 0,
                                      }}
                                    >
                                      {isExpanded ? "▼" : "▶"}
                                    </span>
                                    <div
                                      style={{
                                        fontWeight: "600",
                                        fontSize: "14px",
                                        color: "#1f2937",
                                      }}
                                    >
                                      {med.medication_name}
                                    </div>
                                  </div>
                                  {/* Key Info - Always visible */}
                                  <div
                                    style={{
                                      fontSize: "11px",
                                      color: "#6b7280",
                                      marginLeft: "24px",
                                    }}
                                  >
                                    {med.dose} • {med.route} • {med.frequency} •
                                    Qty: {med.quantity}
                                  </div>
                                </div>

                                {/* Selection buttons - Always visible */}
                                <div
                                  style={{
                                    display: "flex",
                                    gap: "6px",
                                    flexShrink: 0,
                                  }}
                                >
                                  <button
                                    onClick={() =>
                                      handleLocalSelectionChange(
                                        selectionKey,
                                        true,
                                      )
                                    }
                                    style={{
                                      padding: "6px 12px",
                                      border: "1px solid #d1d5db",
                                      borderRadius: "6px",
                                      fontSize: "12px",
                                      fontWeight: "500",
                                      cursor: "pointer",
                                      backgroundColor:
                                        selectionValue === true
                                          ? "#dcfce7"
                                          : "white",
                                      color:
                                        selectionValue === true
                                          ? "#166534"
                                          : "#6b7280",
                                      transition: "all 0.2s",
                                    }}
                                  >
                                    Yes
                                  </button>
                                  <button
                                    onClick={() =>
                                      handleLocalSelectionChange(
                                        selectionKey,
                                        false,
                                      )
                                    }
                                    style={{
                                      padding: "6px 12px",
                                      border: "1px solid #d1d5db",
                                      borderRadius: "6px",
                                      fontSize: "12px",
                                      fontWeight: "500",
                                      cursor: "pointer",
                                      backgroundColor:
                                        selectionValue === false
                                          ? "#fee2e2"
                                          : "white",
                                      color:
                                        selectionValue === false
                                          ? "#991b1b"
                                          : "#6b7280",
                                      transition: "all 0.2s",
                                    }}
                                  >
                                    No
                                  </button>
                                </div>
                              </div>

                              {/* Expanded Details - Conditionally visible */}
                              {isExpanded && (
                                <div
                                  style={{
                                    marginTop: "12px",
                                    marginLeft: "24px",
                                  }}
                                >
                                  {med.reasoning && (
                                    <div
                                      style={{
                                        fontSize: "12px",
                                        color: "#6b7280",
                                        marginBottom: "8px",
                                      }}
                                    >
                                      {med.reasoning}
                                    </div>
                                  )}

                                  {/* Medication Details */}
                                  <div
                                    style={{
                                      marginBottom: "8px",
                                      padding: "8px",
                                      backgroundColor:
                                        "rgba(255, 255, 255, 0.7)",
                                      borderRadius: "6px",
                                    }}
                                  >
                                    <div style={{ marginBottom: "6px" }}>
                                      <span
                                        style={{
                                          fontSize: "11px",
                                          color: "#6b7280",
                                          fontWeight: "600",
                                        }}
                                      >
                                        Dose:{" "}
                                      </span>
                                      <span
                                        style={{
                                          fontSize: "11px",
                                          color: "#1f2937",
                                          fontWeight: "600",
                                        }}
                                      >
                                        {med.dose}
                                      </span>
                                    </div>
                                    <div style={{ marginBottom: "6px" }}>
                                      <span
                                        style={{
                                          fontSize: "11px",
                                          color: "#6b7280",
                                          fontWeight: "600",
                                        }}
                                      >
                                        Route:{" "}
                                      </span>
                                      <span
                                        style={{
                                          fontSize: "11px",
                                          color: "#1f2937",
                                          fontWeight: "600",
                                        }}
                                      >
                                        {med.route}
                                      </span>
                                    </div>
                                    <div style={{ marginBottom: "6px" }}>
                                      <span
                                        style={{
                                          fontSize: "11px",
                                          color: "#6b7280",
                                          fontWeight: "600",
                                        }}
                                      >
                                        Frequency:{" "}
                                      </span>
                                      <span
                                        style={{
                                          fontSize: "11px",
                                          color: "#1f2937",
                                          fontWeight: "600",
                                        }}
                                      >
                                        {med.frequency}
                                      </span>
                                    </div>
                                    <div>
                                      <span
                                        style={{
                                          fontSize: "11px",
                                          color: "#6b7280",
                                          fontWeight: "600",
                                        }}
                                      >
                                        Quantity:{" "}
                                      </span>
                                      <span
                                        style={{
                                          fontSize: "11px",
                                          color: "#1f2937",
                                          fontWeight: "600",
                                        }}
                                      >
                                        {med.quantity}
                                      </span>
                                    </div>
                                  </div>

                                  {/* Quantity Reasoning */}
                                  {med.quantity_reasoning && (
                                    <div
                                      style={{
                                        marginBottom: "8px",
                                        padding: "8px",
                                        backgroundColor:
                                          "rgba(255, 255, 255, 0.7)",
                                        borderRadius: "6px",
                                      }}
                                    >
                                      <div
                                        style={{
                                          fontWeight: "600",
                                          color: "#374151",
                                          fontSize: "11px",
                                          marginBottom: "4px",
                                        }}
                                      >
                                        Quantity Reasoning:
                                      </div>
                                      <div
                                        style={{
                                          color: "#6b7280",
                                          fontSize: "11px",
                                          fontStyle: "italic",
                                        }}
                                      >
                                        {med.quantity_reasoning}
                                      </div>
                                    </div>
                                  )}

                                  {/* Counseling Points */}
                                  {med.counseling_points?.length > 0 && (
                                    <div
                                      style={{
                                        marginBottom: "8px",
                                        padding: "8px",
                                        backgroundColor:
                                          "rgba(255, 255, 255, 0.7)",
                                        borderRadius: "6px",
                                      }}
                                    >
                                      <div
                                        style={{
                                          fontWeight: "600",
                                          color: "#374151",
                                          fontSize: "11px",
                                          marginBottom: "4px",
                                        }}
                                      >
                                        Counseling Points:
                                      </div>
                                      <ul
                                        style={{
                                          margin: 0,
                                          paddingLeft: "16px",
                                          fontSize: "11px",
                                          color: "#6b7280",
                                        }}
                                      >
                                        {med.counseling_points.map(
                                          (point, i) => (
                                            <li
                                              key={i}
                                              style={{ marginBottom: "2px" }}
                                            >
                                              {point}
                                            </li>
                                          ),
                                        )}
                                      </ul>
                                    </div>
                                  )}

                                  {/* Flags Warning */}
                                  {med.flags?.length > 0 && (
                                    <div
                                      style={{
                                        padding: "8px",
                                        backgroundColor: "#fef2f2",
                                        border: "1px solid #fecaca",
                                        borderRadius: "6px",
                                        fontSize: "11px",
                                        color: "#dc2626",
                                        marginBottom: "8px",
                                      }}
                                    >
                                      <div
                                        style={{
                                          fontWeight: "600",
                                          marginBottom: "4px",
                                        }}
                                      >
                                        Important:
                                      </div>
                                      {med.flags.map((flag, i) => (
                                        <div
                                          key={i}
                                          style={{ marginBottom: "2px" }}
                                        >
                                          • {flag}
                                        </div>
                                      ))}
                                    </div>
                                  )}

                                  {/* Pharmacist Note */}
                                  <div>
                                    <label
                                      htmlFor={`note-${selectionKey}`}
                                      style={{
                                        display: "block",
                                        fontSize: "11px",
                                        fontWeight: "600",
                                        color: "#374151",
                                        marginBottom: "4px",
                                      }}
                                    >
                                      Pharmacist Note (Optional):
                                    </label>
                                    <textarea
                                      id={`note-${selectionKey}`}
                                      value={localNotes[selectionKey] || ""}
                                      onChange={(e) =>
                                        handleLocalNoteChange(
                                          selectionKey,
                                          e.target.value,
                                        )
                                      }
                                      onBlur={() => handleSaveNote(selectionKey)}
                                      placeholder="Add any notes or special instructions..."
                                      style={{
                                        width: "100%",
                                        padding: "6px 8px",
                                        border: "1px solid #d1d5db",
                                        borderRadius: "4px",
                                        fontSize: "11px",
                                        resize: "vertical",
                                        minHeight: "50px",
                                        fontFamily: "inherit",
                                      }}
                                    />
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}

                      {/* Pharmacist Added Medications from API (previously saved) */}
                      {(
                        aiRecommendations?.recommendation
                          .pharmacistAddedMedications || []
                      )
                        .filter((medication) => {
                          const selectionKey = `manual-medication-${medication.protocol_id}`;
                          return localSelections[selectionKey] !== false;
                        })
                        .filter(
                          (medication) =>
                            searchTerm === "" ||
                            medication.name
                              .toLowerCase()
                              .includes(searchTerm.toLowerCase()),
                        )
                        .map((medication) => {
                          const selectionKey = `manual-medication-${medication.protocol_id}`;
                          const isExpanded = expandedItems[selectionKey];

                          return (
                            <div
                              key={medication.protocol_id}
                              style={{
                                padding: "12px 14px",
                                borderRadius: "8px",
                                marginBottom: "8px",
                                border: "2px solid #14b8a6",
                                backgroundColor: "#f0fdfa",
                                position: "relative",
                              }}
                            >
                              <div
                                style={{
                                  position: "absolute",
                                  top: "8px",
                                  right: "8px",
                                  padding: "3px 8px",
                                  borderRadius: "4px",
                                  fontSize: "10px",
                                  fontWeight: "700",
                                  backgroundColor: "#14b8a6",
                                  color: "white",
                                }}
                              >
                                PHARMACIST ADDED
                              </div>

                              {/* Collapsed Header - Always Visible */}
                              <div
                                style={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                  gap: "12px",
                                  marginTop: "20px",
                                }}
                              >
                                <div
                                  onClick={() =>
                                    toggleItemExpanded(selectionKey)
                                  }
                                  style={{
                                    flex: 1,
                                    cursor: "pointer",
                                    minWidth: 0,
                                  }}
                                >
                                  <div
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: "8px",
                                      marginBottom: "4px",
                                    }}
                                  >
                                    <span
                                      style={{
                                        fontSize: "16px",
                                        color: "#6b7280",
                                        fontWeight: "bold",
                                        flexShrink: 0,
                                      }}
                                    >
                                      {isExpanded ? "▼" : "▶"}
                                    </span>
                                    <div
                                      style={{
                                        fontWeight: "600",
                                        fontSize: "14px",
                                        color: "#1f2937",
                                      }}
                                    >
                                      {medication.name}
                                    </div>
                                  </div>
                                  {/* Key Info - Always visible */}
                                  <div
                                    style={{
                                      fontSize: "11px",
                                      color: "#6b7280",
                                      marginLeft: "24px",
                                    }}
                                  >
                                    {medication.doses ||
                                      (
                                        enrichedProtocols[
                                          medication.protocol_id
                                        ] as MedicationProtocol
                                      )?.dosing?.adult?.dose ||
                                      "Not specified"}{" "}
                                    •{" "}
                                    {medication.route ||
                                      enrichedProtocols[medication.protocol_id]
                                        ?.dosing?.adult?.route ||
                                      "Not specified"}
                                  </div>
                                </div>

                                {/* Selection buttons */}
                                <div
                                  style={{
                                    display: "flex",
                                    gap: "6px",
                                    flexShrink: 0,
                                  }}
                                >
                                  <button
                                    onClick={() =>
                                      handleLocalSelectionChange(
                                        selectionKey,
                                        true,
                                      )
                                    }
                                    style={{
                                      padding: "6px 12px",
                                      border: "1px solid #d1d5db",
                                      borderRadius: "6px",
                                      fontSize: "12px",
                                      fontWeight: "500",
                                      cursor: "pointer",
                                      backgroundColor:
                                        localSelections[selectionKey] === true
                                          ? "#dcfce7"
                                          : "white",
                                      color:
                                        localSelections[selectionKey] === true
                                          ? "#166534"
                                          : "#6b7280",
                                      transition: "all 0.2s",
                                    }}
                                  >
                                    Yes
                                  </button>
                                  <button
                                    onClick={() =>
                                      handleLocalSelectionChange(
                                        selectionKey,
                                        false,
                                      )
                                    }
                                    style={{
                                      padding: "6px 12px",
                                      border: "1px solid #d1d5db",
                                      borderRadius: "6px",
                                      fontSize: "12px",
                                      fontWeight: "500",
                                      cursor: "pointer",
                                      backgroundColor:
                                        localSelections[selectionKey] === false
                                          ? "#fee2e2"
                                          : "white",
                                      color:
                                        localSelections[selectionKey] === false
                                          ? "#991b1b"
                                          : "#6b7280",
                                      transition: "all 0.2s",
                                    }}
                                  >
                                    No
                                  </button>
                                </div>
                              </div>

                              {/* Expanded Details - Conditionally visible */}
                              {isExpanded && (
                                <div
                                  style={{
                                    marginTop: "12px",
                                    marginLeft: "24px",
                                  }}
                                >
                                  {/* Medication Details */}
                                  <div
                                    style={{
                                      marginBottom: "8px",
                                      padding: "8px",
                                      backgroundColor:
                                        "rgba(255, 255, 255, 0.7)",
                                      borderRadius: "6px",
                                      display: "grid",
                                      gridTemplateColumns: "80px 1fr",
                                      gap: "6px",
                                    }}
                                  >
                                    <div>
                                      <span
                                        style={{
                                          fontSize: "11px",
                                          color: "#6b7280",
                                          fontWeight: "600",
                                        }}
                                      >
                                        Route:{" "}
                                      </span>
                                      <span
                                        style={{
                                          fontSize: "11px",
                                          color: "#1f2937",
                                          fontWeight: "600",
                                        }}
                                      >
                                        {medication.route ||
                                          enrichedProtocols[
                                            medication.protocol_id
                                          ]?.dosing?.adult?.route ||
                                          "Not specified"}
                                      </span>
                                    </div>
                                    <div>
                                      <span
                                        style={{
                                          fontSize: "11px",
                                          color: "#6b7280",
                                          fontWeight: "600",
                                        }}
                                      >
                                        Doses:{" "}
                                      </span>
                                      <span
                                        style={{
                                          fontSize: "11px",
                                          color: "#1f2937",
                                          fontWeight: "600",
                                        }}
                                      >
                                        {medication.doses ||
                                          (
                                            enrichedProtocols[
                                              medication.protocol_id
                                            ] as MedicationProtocol
                                          )?.dosing?.adult?.dose ||
                                          "Not specified"}
                                      </span>
                                    </div>
                                  </div>

                                  {/* Brands */}
                                  {medication.brands &&
                                    medication.brands.length > 0 && (
                                      <div
                                        style={{
                                          marginBottom: "8px",
                                          padding: "8px",
                                          backgroundColor:
                                            "rgba(255, 255, 255, 0.7)",
                                          borderRadius: "6px",
                                        }}
                                      >
                                        <div
                                          style={{
                                            fontWeight: "600",
                                            color: "#374151",
                                            fontSize: "11px",
                                            marginBottom: "4px",
                                          }}
                                        >
                                          Brands:
                                        </div>
                                        <div
                                          style={{
                                            color: "#6b7280",
                                            fontSize: "11px",
                                          }}
                                        >
                                          {medication.brands.join(", ")}
                                        </div>
                                      </div>
                                    )}

                                  {/* Pharmacist Note */}
                                  <div>
                                    <label
                                      htmlFor={`note-${selectionKey}`}
                                      style={{
                                        display: "block",
                                        fontSize: "11px",
                                        fontWeight: "600",
                                        color: "#374151",
                                        marginBottom: "4px",
                                      }}
                                    >
                                      Pharmacist Note (Optional):
                                    </label>
                                    <textarea
                                      id={`note-${selectionKey}`}
                                      value={localNotes[selectionKey] || ""}
                                      onChange={(e) =>
                                        handleLocalNoteChange(
                                          selectionKey,
                                          e.target.value,
                                        )
                                      }
                                      onBlur={() => handleSaveNote(selectionKey)}
                                      placeholder="Add any notes or special instructions..."
                                      style={{
                                        width: "100%",
                                        padding: "6px 8px",
                                        border: "1px solid #d1d5db",
                                        borderRadius: "4px",
                                        fontSize: "11px",
                                        resize: "vertical",
                                        minHeight: "50px",
                                        fontFamily: "inherit",
                                      }}
                                    />
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}

                      {/* Manual Medication Selections (newly added in this session) */}
                      {localManualSelections
                        .filter((s) => s.type === "medication")
                        .filter(
                          (selection) =>
                            searchTerm === "" ||
                            selection.protocol.displayName
                              .toLowerCase()
                              .includes(searchTerm.toLowerCase()),
                        )
                        // Filter out items that are already in pharmacistAddedMedications
                        .filter(
                          (selection) =>
                            !(
                              aiRecommendations?.recommendation
                                .pharmacistAddedMedications || []
                            ).some(
                              (m) =>
                                m.protocol_id === selection.protocol.protocolId,
                            ),
                        )
                        // Filter out items where user selected "No" (false)
                        .filter((selection) => {
                          const selectionKey = `manual-medication-${selection.protocol.protocolId}`;
                          return localSelections[selectionKey] !== false;
                        })
                        .map((selection) => {
                          const selectionKey = `manual-medication-${selection.protocol.protocolId}`;
                          const isExpanded = expandedItems[selectionKey];
                          const protocol =
                            selection.protocol as MedicationProtocol;

                          return (
                            <div
                              key={selection.protocol.protocolId}
                              style={{
                                padding: "12px 14px",
                                borderRadius: "8px",
                                marginBottom: "8px",
                                border: "2px dashed #14b8a6",
                                backgroundColor: "#f0fdfa",
                                position: "relative",
                              }}
                            >
                              {/* Collapsed Header - Always Visible */}
                              <div
                                style={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                  gap: "12px",
                                }}
                              >
                                <div
                                  onClick={() =>
                                    toggleItemExpanded(selectionKey)
                                  }
                                  style={{
                                    flex: 1,
                                    cursor: "pointer",
                                    minWidth: 0,
                                  }}
                                >
                                  <div
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: "8px",
                                      marginBottom: "4px",
                                    }}
                                  >
                                    <span
                                      style={{
                                        fontSize: "16px",
                                        color: "#6b7280",
                                        fontWeight: "bold",
                                        flexShrink: 0,
                                      }}
                                    >
                                      {isExpanded ? "▼" : "▶"}
                                    </span>
                                    <div
                                      style={{
                                        fontWeight: "600",
                                        fontSize: "14px",
                                        color: "#1f2937",
                                      }}
                                    >
                                      {selection.protocol.displayName}
                                    </div>
                                  </div>
                                  {/* Key Info - Always visible */}
                                  <div
                                    style={{
                                      fontSize: "11px",
                                      color: "#6b7280",
                                      marginLeft: "24px",
                                    }}
                                  >
                                    {getStrength(protocol)} •{" "}
                                    {getRoute(protocol)} • {getSig(protocol)}
                                  </div>
                                </div>

                                {/* Selection buttons - Yes keeps it, No/Remove removes it */}
                                <div
                                  style={{
                                    display: "flex",
                                    gap: "6px",
                                    flexShrink: 0,
                                  }}
                                >
                                  <button
                                    onClick={() =>
                                      handleLocalSelectionChange(
                                        selectionKey,
                                        true,
                                      )
                                    }
                                    style={{
                                      padding: "6px 12px",
                                      border: "1px solid #d1d5db",
                                      borderRadius: "6px",
                                      fontSize: "12px",
                                      fontWeight: "500",
                                      cursor: "pointer",
                                      backgroundColor:
                                        localSelections[selectionKey] === true
                                          ? "#dcfce7"
                                          : "white",
                                      color:
                                        localSelections[selectionKey] === true
                                          ? "#166534"
                                          : "#6b7280",
                                      transition: "all 0.2s",
                                    }}
                                  >
                                    Yes
                                  </button>
                                  <button
                                    onClick={() => {
                                      // Clicking No on a manually added item removes it
                                      handleLocalSelectionChange(
                                        selectionKey,
                                        false,
                                      );
                                      handleLocalManualSelectionRemove(
                                        selection.protocol.protocolId,
                                      );
                                    }}
                                    style={{
                                      padding: "6px 12px",
                                      backgroundColor: "#fee2e2",
                                      color: "#991b1b",
                                      border: "none",
                                      borderRadius: "6px",
                                      fontSize: "12px",
                                      fontWeight: "600",
                                      cursor: "pointer",
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    No / Remove
                                  </button>
                                </div>
                              </div>

                              {/* Expanded Details - Conditionally visible */}
                              {isExpanded && (
                                <div
                                  style={{
                                    marginTop: "12px",
                                    marginLeft: "24px",
                                  }}
                                >
                                  {/* Protocol Information */}
                                  {protocol.notes && (
                                    <div
                                      style={{
                                        fontSize: "12px",
                                        color: "#6b7280",
                                        marginBottom: "8px",
                                      }}
                                    >
                                      {protocol.notes}
                                    </div>
                                  )}

                                  {/* Medication Details */}
                                  <div
                                    style={{
                                      marginBottom: "8px",
                                      padding: "8px",
                                      backgroundColor:
                                        "rgba(255, 255, 255, 0.7)",
                                      borderRadius: "6px",
                                    }}
                                  >
                                    <div style={{ marginBottom: "6px" }}>
                                      <span
                                        style={{
                                          fontSize: "11px",
                                          color: "#6b7280",
                                          fontWeight: "600",
                                        }}
                                      >
                                        Strength:{" "}
                                      </span>
                                      <span
                                        style={{
                                          fontSize: "11px",
                                          color: "#1f2937",
                                          fontWeight: "600",
                                        }}
                                      >
                                        {getStrength(protocol)}
                                      </span>
                                    </div>
                                    <div style={{ marginBottom: "6px" }}>
                                      <span
                                        style={{
                                          fontSize: "11px",
                                          color: "#6b7280",
                                          fontWeight: "600",
                                        }}
                                      >
                                        Route:{" "}
                                      </span>
                                      <span
                                        style={{
                                          fontSize: "11px",
                                          color: "#1f2937",
                                          fontWeight: "600",
                                        }}
                                      >
                                        {getRoute(protocol)}
                                      </span>
                                    </div>
                                    <div>
                                      <span
                                        style={{
                                          fontSize: "11px",
                                          color: "#6b7280",
                                          fontWeight: "600",
                                        }}
                                      >
                                        Sig:{" "}
                                      </span>
                                      <span
                                        style={{
                                          fontSize: "11px",
                                          color: "#1f2937",
                                          fontWeight: "600",
                                        }}
                                      >
                                        {getSig(protocol)}
                                      </span>
                                    </div>
                                  </div>

                                  {/* Brands */}
                                  <div
                                    style={{
                                      marginBottom: "8px",
                                      padding: "8px",
                                      backgroundColor:
                                        "rgba(255, 255, 255, 0.7)",
                                      borderRadius: "6px",
                                    }}
                                  >
                                    <div
                                      style={{
                                        fontWeight: "600",
                                        color: "#374151",
                                        fontSize: "11px",
                                        marginBottom: "4px",
                                      }}
                                    >
                                      Brands:
                                    </div>
                                    <div
                                      style={{
                                        color: "#6b7280",
                                        fontSize: "11px",
                                      }}
                                    >
                                      {extractBrandName(protocol.displayName)}
                                    </div>
                                  </div>

                                  {/* Quantity */}
                                  {selection.type === "medication" && (
                                    <div>
                                      <label
                                        htmlFor={`quantity-${selectionKey}`}
                                        style={{
                                          display: "block",
                                          fontSize: "11px",
                                          fontWeight: "600",
                                          color: "#374151",
                                          marginBottom: "4px",
                                        }}
                                      >
                                        Quantity:
                                      </label>
                                      <input
                                        id={`quantity-${selectionKey}`}
                                        type="text"
                                        inputMode="numeric"
                                        value={localQuantities[selectionKey] || ""}
                                        onChange={(e) => {
                                          const val = e.target.value;
                                          // Allow empty string (clearing) or digits only
                                          if (val === "" || /^\d+$/.test(val)) {
                                            handleLocalQuantityChange(selectionKey, val);
                                          }
                                        }}
                                        placeholder="e.g., 30"
                                        style={{
                                          width: "100%",
                                          padding: "6px 8px",
                                          border: "1px solid #d1d5db",
                                          borderRadius: "4px",
                                          fontSize: "11px",
                                          fontFamily: "inherit",
                                        }}
                                      />
                                    </div>
                                  )}

                                  {/* Pharmacist Note */}
                                  <div>
                                    <label
                                      htmlFor={`note-${selectionKey}`}
                                      style={{
                                        display: "block",
                                        fontSize: "11px",
                                        fontWeight: "600",
                                        color: "#374151",
                                        marginBottom: "4px",
                                      }}
                                    >
                                      Pharmacist Note (Optional):
                                    </label>
                                    <textarea
                                      id={`note-${selectionKey}`}
                                      value={localNotes[selectionKey] || ""}
                                      onChange={(e) =>
                                        handleLocalNoteChange(
                                          selectionKey,
                                          e.target.value,
                                        )
                                      }
                                      onBlur={() => handleSaveNote(selectionKey)}
                                      placeholder="Add any notes or special instructions..."
                                      style={{
                                        width: "100%",
                                        padding: "6px 8px",
                                        border: "1px solid #d1d5db",
                                        borderRadius: "4px",
                                        fontSize: "11px",
                                        resize: "vertical",
                                        minHeight: "50px",
                                        fontFamily: "inherit",
                                      }}
                                    />
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                    </div>
                  </div>
                )}

              {/* Manual Add Section - Always shown */}
              <div
                style={{
                  padding: "16px 20px",
                  backgroundColor: "#f9fafb",
                }}
              >
                <div
                  style={{
                    marginBottom: "16px",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginBottom: "12px",
                    }}
                  >
                    <div>
                      <h4
                        style={{
                          fontSize: "16px",
                          fontWeight: "700",
                          color: "#1f2937",
                          marginBottom: "4px",
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                        }}
                      >
                        Browse Additional Items
                      </h4>
                      <p
                        style={{
                          fontSize: "12px",
                          color: "#6b7280",
                          margin: 0,
                        }}
                      >
                        Add vaccines or medications not in AI recommendations
                      </p>
                    </div>
                    {!loadingProtocols &&
                      (filteredProtocols.vaccines.length > 0 ||
                        filteredProtocols.medications.length > 0) && (
                        // <button
                        //   onClick={() => {
                        //     // Add all visible items that aren't already added or in AI
                        //     filteredProtocols.vaccines.forEach((vaccine) => {
                        //       if (
                        //         !isItemAlreadyAdded(vaccine.protocolId) &&
                        //         !isItemInAIRecommendations(vaccine.protocolId)
                        //       ) {
                        //         handleAddManualItem("vaccine", vaccine);
                        //       }
                        //     });
                        //     filteredProtocols.medications.forEach(
                        //       (medication) => {
                        //         if (
                        //           !isItemAlreadyAdded(
                        //             medication.protocolId
                        //           ) &&
                        //           !isItemInAIRecommendations(
                        //             medication.protocolId
                        //           )
                        //         ) {
                        //           handleAddManualItem(
                        //             "medication",
                        //             medication
                        //           );
                        //         }
                        //       }
                        //     );
                        //   }}
                        //   style={{
                        //     padding: "8px 16px",
                        //     backgroundColor: "#14b8a6",
                        //     color: "white",
                        //     border: "none",
                        //     borderRadius: "6px",
                        //     fontSize: "13px",
                        //     fontWeight: "600",
                        //     cursor: "pointer",
                        //     whiteSpace: "nowrap",
                        //   }}
                        // >
                        //   Add All Visible
                        // </button>
                        <div />
                      )}
                  </div>

                  {/* Search and Filter Controls */}
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "10px",
                    }}
                  >
                    <select
                      value={selectedType}
                      onChange={(e) =>
                        setSelectedType(
                          e.target.value as "all" | "vaccine" | "medication",
                        )
                      }
                      style={{
                        width: "100%",
                        padding: "12px 16px",
                        border: "2px solid #d1d5db",
                        borderRadius: "8px",
                        fontSize: "15px",
                        backgroundColor: "white",
                        cursor: "pointer",
                      }}
                    >
                      <option value="all">All Types</option>
                      <option value="vaccine">Vaccines Only</option>
                      <option value="medication">Medications Only</option>
                    </select>
                    <input
                      type="text"
                      placeholder="Search vaccines or medications..."
                      value={searchTerm}
                      onChange={(e) => {
                        const newValue = e.target.value;
                        setSearchTerm(newValue);
                      }}
                      style={{
                        width: "100%",
                        padding: "12px 16px",
                        border: "2px solid #d1d5db",
                        borderRadius: "8px",
                        fontSize: "15px",
                        outline: "none",
                        transition: "border-color 0.2s",
                        color: "#1f2937",
                        backgroundColor: "white",
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = "#14b8a6";
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = "#d1d5db";
                      }}
                    />
                  </div>
                </div>

                {loadingProtocols && (
                  <div
                    style={{
                      padding: "20px",
                      textAlign: "center",
                      color: "#6b7280",
                    }}
                  >
                    Loading available items...
                  </div>
                )}

                {!loadingProtocols && (
                  <div
                    style={{
                      maxHeight: "400px",
                      overflowY: "auto",
                      border: "1px solid #e5e7eb",
                      borderRadius: "8px",
                      backgroundColor: "white",
                    }}
                  >
                    {/* Manual Vaccines */}
                    {filteredProtocols.vaccines.length > 0 && (
                      <div style={{ borderBottom: "1px solid #e5e7eb" }}>
                        <div
                          style={{
                            padding: "8px 12px",
                            backgroundColor: "#f3f4f6",
                            fontWeight: "600",
                            fontSize: "12px",
                            color: "#374151",
                            position: "sticky",
                            top: 0,
                          }}
                        >
                          VACCINES ({filteredProtocols.vaccines.length})
                        </div>
                        {filteredProtocols.vaccines.map((vaccine) => {
                          const alreadyAdded = isItemAlreadyAdded(
                            vaccine.protocolId,
                          );
                          const inAI = isItemInAIRecommendations(
                            vaccine.protocolId,
                          );
                          return (
                            <div
                              key={vaccine.protocolId}
                              style={{
                                padding: "10px 12px",
                                borderBottom: "1px solid #f3f4f6",
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                              }}
                            >
                              <div style={{ flex: 1 }}>
                                <div
                                  style={{
                                    fontWeight: "600",
                                    fontSize: "13px",
                                    color: "#1f2937",
                                  }}
                                >
                                  {vaccine.displayName}
                                </div>
                                <div
                                  style={{
                                    fontSize: "11px",
                                    color: "#6b7280",
                                    marginTop: "2px",
                                  }}
                                >
                                  {extractBrandName(vaccine.displayName)} •{" "}
                                  {getRoute(vaccine)} • {getDoseInfo(vaccine)}
                                </div>
                              </div>
                              {inAI ? (
                                <span
                                  style={{
                                    padding: "4px 10px",
                                    borderRadius: "4px",
                                    fontSize: "11px",
                                    fontWeight: "600",
                                    backgroundColor: "#dbeafe",
                                    color: "#1e40af",
                                  }}
                                >
                                  In AI Section
                                </span>
                              ) : alreadyAdded ? (
                                <div
                                  style={{
                                    display: "flex",
                                    gap: "6px",
                                    alignItems: "center",
                                  }}
                                >
                                  <span
                                    style={{
                                      padding: "4px 10px",
                                      borderRadius: "4px",
                                      fontSize: "11px",
                                      fontWeight: "600",
                                      backgroundColor: "#d1fae5",
                                      color: "#065f46",
                                    }}
                                  >
                                    Added
                                  </span>
                                  <button
                                    onClick={() =>
                                      handleLocalManualSelectionRemove(
                                        vaccine.protocolId,
                                      )
                                    }
                                    style={{
                                      padding: "4px 8px",
                                      backgroundColor: "#fee2e2",
                                      color: "#991b1b",
                                      border: "none",
                                      borderRadius: "4px",
                                      fontSize: "11px",
                                      fontWeight: "600",
                                      cursor: "pointer",
                                    }}
                                  >
                                    Remove
                                  </button>
                                </div>
                              ) : (
                                <button
                                  onClick={() =>
                                    handleAddManualItem("vaccine", vaccine)
                                  }
                                  style={{
                                    padding: "4px 12px",
                                    backgroundColor: "#14b8a6",
                                    color: "white",
                                    border: "none",
                                    borderRadius: "4px",
                                    fontSize: "11px",
                                    fontWeight: "600",
                                    cursor: "pointer",
                                  }}
                                >
                                  Add
                                </button>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {/* Manual Medications */}
                    {filteredProtocols.medications.length > 0 && (
                      <div>
                        <div
                          style={{
                            padding: "8px 12px",
                            backgroundColor: "#f3f4f6",
                            fontWeight: "600",
                            fontSize: "12px",
                            color: "#374151",
                            position: "sticky",
                            top: 0,
                          }}
                        >
                          MEDICATIONS ({filteredProtocols.medications.length})
                        </div>
                        {filteredProtocols.medications.map((medication) => {
                          const alreadyAdded = isItemAlreadyAdded(
                            medication.protocolId,
                          );
                          const inAI = isItemInAIRecommendations(
                            medication.protocolId,
                          );
                          return (
                            <div
                              key={medication.protocolId}
                              style={{
                                padding: "10px 12px",
                                borderBottom: "1px solid #f3f4f6",
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                              }}
                            >
                              <div style={{ flex: 1 }}>
                                <div
                                  style={{
                                    fontWeight: "600",
                                    fontSize: "13px",
                                    color: "#1f2937",
                                  }}
                                >
                                  {medication.displayName}
                                </div>
                                <div
                                  style={{
                                    fontSize: "11px",
                                    color: "#6b7280",
                                    marginTop: "2px",
                                  }}
                                >
                                  {extractBrandName(medication.displayName)} •{" "}
                                  {getStrength(medication)} •{" "}
                                  {getRoute(medication)}
                                </div>
                              </div>
                              {inAI ? (
                                <span
                                  style={{
                                    padding: "4px 10px",
                                    borderRadius: "4px",
                                    fontSize: "11px",
                                    fontWeight: "600",
                                    backgroundColor: "#dbeafe",
                                    color: "#1e40af",
                                  }}
                                >
                                  In AI Section
                                </span>
                              ) : alreadyAdded ? (
                                <div
                                  style={{
                                    display: "flex",
                                    gap: "6px",
                                    alignItems: "center",
                                  }}
                                >
                                  <span
                                    style={{
                                      padding: "4px 10px",
                                      borderRadius: "4px",
                                      fontSize: "11px",
                                      fontWeight: "600",
                                      backgroundColor: "#d1fae5",
                                      color: "#065f46",
                                    }}
                                  >
                                    Added
                                  </span>
                                  <button
                                    onClick={() =>
                                      handleLocalManualSelectionRemove(
                                        medication.protocolId,
                                      )
                                    }
                                    style={{
                                      padding: "4px 8px",
                                      backgroundColor: "#fee2e2",
                                      color: "#991b1b",
                                      border: "none",
                                      borderRadius: "4px",
                                      fontSize: "11px",
                                      fontWeight: "600",
                                      cursor: "pointer",
                                    }}
                                  >
                                    Remove
                                  </button>
                                </div>
                              ) : (
                                <button
                                  onClick={() =>
                                    handleAddManualItem(
                                      "medication",
                                      medication,
                                    )
                                  }
                                  style={{
                                    padding: "4px 12px",
                                    backgroundColor: "#14b8a6",
                                    color: "white",
                                    border: "none",
                                    borderRadius: "4px",
                                    fontSize: "11px",
                                    fontWeight: "600",
                                    cursor: "pointer",
                                  }}
                                >
                                  Add
                                </button>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {filteredProtocols.vaccines.length === 0 &&
                      filteredProtocols.medications.length === 0 && (
                        <div
                          style={{
                            padding: "40px 20px",
                            textAlign: "center",
                            color: "#9ca3af",
                            fontSize: "13px",
                          }}
                        >
                          {searchTerm
                            ? "No items found matching your search."
                            : "No items available."}
                        </div>
                      )}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
